import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import SEO from '@/components/university/common/SEO'
import UniversityHeader from '@/components/university/layout/UniversityHeader'
import UniversityFooter from '@/components/university/layout/UniversityFooter';

const M = '#06060c'
const D = '#0a0a14'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

const getGiftPlans = (t: any) => [
  {
    key: 'gift_1month',
    plan_key: 'university_monthly',
    duration_months: 1,
    price: '$29',
    amount: 2900,
    label: t('university.gift.plans.month_1.label', 'Gift 1 Month'),
    sublabel: t('university.gift.plans.month_1.sublabel', 'All 45 degrees · 1 month'),
    emoji: '🎁',
    description: t('university.gift.plans.month_1.description', 'A month of unlimited access to every degree programme. Perfect for trying Still Waters University.'),
    features: [
      t('university.gift.plans.month_1.f1', 'All 45 degree programmes'),
      t('university.gift.plans.month_1.f2', 'All modules & lessons'),
      t('university.gift.plans.month_1.f3', 'Degree certificates'),
      t('university.gift.plans.month_1.f4', 'AI professor sessions')
    ],
    highlight: false,
    badge: null,
  },
  {
    key: 'gift_6month',
    plan_key: 'scholar_pass_6m',
    duration_months: 6,
    price: '$99',
    amount: 9900,
    label: t('university.gift.plans.month_6.label', 'Gift 6 Months'),
    sublabel: t('university.gift.plans.month_6.sublabel', 'All 45 degrees · 6 months'),
    emoji: '🎓',
    description: t('university.gift.plans.month_6.description', 'Six months to complete a full degree — or explore multiple programmes. Enough to change a life.'),
    features: [
      t('university.gift.plans.month_6.f1', 'All 45 degree programmes'),
      t('university.gift.plans.month_6.f2', '6 months full access'),
      t('university.gift.plans.month_6.f3', 'All certificates'),
      t('university.gift.plans.month_6.f4', 'AI professor — unlimited'),
      t('university.gift.plans.month_6.f5', 'Beautiful gift certificate')
    ],
    highlight: true,
    badge: t('university.gift.badges.mostGifted', 'MOST GIFTED'),
  },
  {
    key: 'gift_1year',
    plan_key: 'scholar_pass_12m',
    duration_months: 12,
    price: '$179',
    amount: 17900,
    label: t('university.gift.plans.month_12.label', 'Gift a Full Year'),
    sublabel: t('university.gift.plans.month_12.sublabel', 'All 45 degrees · 1 year'),
    emoji: '⭐',
    description: t('university.gift.plans.month_12.description', 'The ultimate gift — a full year of education. Complete multiple degrees and earn University Honours.'),
    features: [
      t('university.gift.plans.month_12.f1', 'All 45 degree programmes'),
      t('university.gift.plans.month_12.f2', '12 months full access'),
      t('university.gift.plans.month_12.f3', 'All certificates & Honours'),
      t('university.gift.plans.month_12.f4', 'Priority new degrees'),
      t('university.gift.plans.month_12.f5', 'Save $169 vs 12× monthly')
    ],
    highlight: false,
    badge: t('university.gift.badges.bestValue', 'BEST VALUE'),
  },
]

const CAUSE_TAGS = [
  { value: 'general', label: '💙 General fund' },
  { value: 'domestic_abuse', label: '🏠 Domestic abuse survivors' },
  { value: 'refugee', label: '🌍 Refugees & asylum seekers' },
  { value: 'youth', label: '🌱 At-risk youth' },
  { value: 'prison', label: '🔓 Prison rehabilitation' },
  { value: 'homelessness', label: '🤝 Homelessness recovery' },
]

export default function GiftPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()

  const GIFT_PLANS = getGiftPlans(t)

  const [isMobile, setIsMobile] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState('gift_6month')
  const [step, setStep] = useState<'choose' | 'details'>('choose')

  // Recipient details
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [personalMessage, setPersonalMessage] = useState('')
  const [buyerEmail, setBuyerEmail] = useState(user?.email || '')
  const [buyerName, setBuyerName] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check(); window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (user?.email) setBuyerEmail(user.email)
  }, [user])

  const plan = GIFT_PLANS.find(p => p.key === selectedPlan)!

  async function handleCheckout() {
    if (!recipientEmail.trim()) { setError(t('university.gift.errorRecipient', 'Please enter the recipient\'s email address.')); return }
    if (!buyerEmail.trim()) { setError(t('university.gift.errorBuyerEmail', 'Please enter your email address.')); return }

    setLoading(true); setError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()

      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl

      const res = await fetch(`${supabaseUrl}/functions/v1/gift-enrolment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          recipient_email: recipientEmail.trim(),
          recipient_name: recipientName.trim() || undefined,
          personal_message: personalMessage.trim() || undefined,
          plan_key: plan.plan_key,
          duration_months: plan.duration_months,
          buyer_email: buyerEmail.trim(),
          buyer_name: buyerName.trim() || undefined,
          success_url: `${window.location.origin}/gift/sent`,
          cancel_url: `${window.location.origin}/gift`,
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Checkout failed')
      if (data.url) window.location.href = data.url
    } catch (err) {
      setError((err as Error).message)
      setLoading(false)
    }
  }

  return (
    <>
    <UniversityHeader/>
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        title="Give the Gift of Knowledge"
        description="Share the transformation of education. Buy a scholarship or gift a full degree to someone you care about. A life-changing gift starting from $29."
      />

      {/* Hero */}
      <div style={{ borderBottom: `1px solid ${BR}`, background: D }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '40px 20px 32px' : '56px 24px 40px', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎁</div>
            <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2.5px', marginBottom: 10 }}>
              {t('university.gift.eyebrow', 'GIFT OF EDUCATION')}
            </p>
            <h1 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 900, margin: '0 0 14px', letterSpacing: '-1px', fontFamily: "'Playfair Display', Georgia, serif" }}>
              {t('university.gift.title', 'Give the Gift of a Degree')}
            </h1>
            <p style={{ color: MU, fontSize: isMobile ? 14 : 16, lineHeight: 1.7, maxWidth: 520, margin: '0 auto' }}>
              {t('university.gift.subtitle', 'Buy an education for someone you care about. They receive a gift email, click to claim, and start learning immediately.')}
            </p>
          </motion.div>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '48px 24px 80px' }}>
        <AnimatePresence mode="wait">

          {/* ── Step 1: Choose plan ─────────────────────────────────────────── */}
          {step === 'choose' && (
            <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p style={{ fontWeight: 700, fontSize: 13, color: MU, marginBottom: 20, letterSpacing: '0.5px' }}>
                {t('university.gift.stepChoose', 'STEP 1 — CHOOSE WHAT TO GIFT')}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
                {GIFT_PLANS.map(p => (
                  <motion.div key={p.key} whileHover={{ y: -3 }} onClick={() => setSelectedPlan(p.key)}
                    style={{ background: selectedPlan === p.key ? 'rgba(212,168,67,0.07)' : CA, border: `${selectedPlan === p.key ? 2 : 1}px solid ${selectedPlan === p.key ? G : BR}`, borderRadius: 20, padding: '24px 20px', cursor: 'pointer', position: 'relative', transition: 'all 0.2s' }}>
                    {p.badge && (
                      <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', background: p.highlight ? IN : G, color: p.highlight ? '#fff' : '#06060c', borderRadius: 100, padding: '3px 12px', fontSize: 10, fontWeight: 800, letterSpacing: '1px', whiteSpace: 'nowrap' }}>
                        {p.badge}
                      </div>
                    )}
                    <div style={{ fontSize: 28, marginBottom: 12 }}>{p.emoji}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                      <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1px', lineHeight: 1 }}>{p.price}</span>
                    </div>
                    <p style={{ fontWeight: 800, fontSize: 15, margin: '0 0 2px' }}>{p.label}</p>
                    <p style={{ fontSize: 12, color: MU, margin: '0 0 12px' }}>{p.sublabel}</p>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, margin: '0 0 16px' }}>{p.description}</p>
                    <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {p.features.map(f => (
                        <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <span style={{ color: G, fontSize: 11, fontWeight: 900, flexShrink: 0 }}>✓</span>
                          <span style={{ fontSize: 12, color: MU }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 16, width: 20, height: 20, borderRadius: '50%', border: `2px solid ${selectedPlan === p.key ? G : BR}`, background: selectedPlan === p.key ? G : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                      {selectedPlan === p.key && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#06060c' }} />}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pay It Forward nudge */}
              <div style={{ background: 'rgba(14,165,233,0.06)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 16, padding: isMobile ? '16px' : '20px 24px', marginBottom: 32, display: 'flex', alignItems: 'center', gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ fontSize: 32, flexShrink: 0 }}>💙</div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 4px' }}>
                    {t('university.gift.payForwardNudge', "Don't have someone specific in mind?")}
                  </p>
                  <p style={{ fontSize: 13, color: MU, margin: 0, lineHeight: 1.5 }}>
                    {t('university.gift.payForwardNudgeText', 'Pay It Forward — fund education for a woman escaping domestic abuse, a refugee, or someone who simply cannot afford it.')}
                  </p>
                </div>
                <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate('/university/give')}
                  style={{ background: 'rgba(14,165,233,0.15)', border: '1px solid rgba(14,165,233,0.35)', borderRadius: 12, padding: '10px 20px', color: '#7dd3fc', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {t('university.gift.payForwardBtn', 'Pay It Forward →')}
                </motion.button>
              </div>

              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={() => setStep('details')}
                style={{ width: '100%', background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '18px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(212,168,67,0.3)' }}>
                {t('university.gift.continueBtn', 'Continue — Add Recipient Details →')}
              </motion.button>
            </motion.div>
          )}

          {/* ── Step 2: Recipient details ───────────────────────────────────── */}
          {step === 'details' && (
            <motion.div key="details" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <button onClick={() => setStep('choose')} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BR}`, color: TX, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 10, marginBottom: 28 }}>
                ← {t('university.gift.back', 'Back')}
              </button>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 28, alignItems: 'start' }}>

                {/* Left — form */}
                <div>
                  <p style={{ fontWeight: 700, fontSize: 13, color: MU, marginBottom: 24, letterSpacing: '0.5px' }}>
                    {t('university.gift.stepDetails', 'STEP 2 — RECIPIENT & MESSAGE')}
                  </p>

                  {/* Recipient */}
                  <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '24px', marginBottom: 20 }}>
                    <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 20 }}>
                      {t('university.gift.recipientSection', 'WHO IS THE GIFT FOR?')}
                    </p>
                    {[
                      { label: t('university.gift.recipientEmail', 'Their Email Address *'), value: recipientEmail, set: setRecipientEmail, placeholder: 'friend@email.com', type: 'email', required: true },
                      { label: t('university.gift.recipientName', 'Their Name (optional)'), value: recipientName, set: setRecipientName, placeholder: 'Jane Smith', type: 'text', required: false },
                    ].map(field => (
                      <div key={field.label} style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>{field.label}</label>
                        <input value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder} type={field.type}
                          style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                      </div>
                    ))}
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>
                        {t('university.gift.message', 'Personal Message (optional)')}
                      </label>
                      <textarea value={personalMessage} onChange={e => setPersonalMessage(e.target.value)}
                        placeholder={t('university.gift.messagePlaceholder', "e.g. I thought this would help you on your new journey. I believe in you!")}
                        rows={3}
                        style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', resize: 'none', boxSizing: 'border-box', lineHeight: 1.5 }} />
                    </div>
                  </div>

                  {/* Buyer (if not logged in) */}
                  {!user && (
                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '24px', marginBottom: 20 }}>
                      <p style={{ fontSize: 12, fontWeight: 800, color: G, letterSpacing: '1.5px', marginBottom: 20 }}>
                        {t('university.gift.buyerSection', 'YOUR DETAILS')}
                      </p>
                      {[
                        { label: t('university.gift.buyerEmail', 'Your Email Address *'), value: buyerEmail, set: setBuyerEmail, placeholder: 'you@email.com', type: 'email' },
                        { label: t('university.gift.buyerName', 'Your Name (optional)'), value: buyerName, set: setBuyerName, placeholder: 'Your name', type: 'text' },
                      ].map(field => (
                        <div key={field.label} style={{ marginBottom: 16 }}>
                          <label style={{ fontSize: 12, fontWeight: 700, color: MU, display: 'block', marginBottom: 6 }}>{field.label}</label>
                          <input value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder} type={field.type}
                            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${BR}`, background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>
                  )}

                  <AnimatePresence>
                    {error && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#f87171' }}>
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={handleCheckout} disabled={loading}
                    style={{ width: '100%', background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '18px', fontSize: 16, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, boxShadow: '0 6px 24px rgba(212,168,67,0.3)' }}>
                    {loading ? t('university.gift.processing', 'Processing…') : t('university.gift.payBtn', `Pay ${plan.price} & Send Gift →`)}
                  </motion.button>
                  <p style={{ fontSize: 12, color: MU, textAlign: 'center', marginTop: 12 }}>
                    🔒 {t('university.gift.secure', 'Secure checkout via Stripe · Recipient gets a gift email instantly')}
                  </p>
                </div>

                {/* Right — summary */}
                <div style={{ background: 'rgba(212,168,67,0.04)', border: '1.5px solid rgba(212,168,67,0.2)', borderRadius: 20, padding: '24px', position: 'sticky', top: 90 }}>
                  <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '1.5px', marginBottom: 16 }}>
                    {t('university.gift.summaryTitle', 'GIFT SUMMARY')}
                  </p>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{plan.emoji}</div>
                  <p style={{ fontWeight: 800, fontSize: 17, margin: '0 0 4px' }}>{plan.label}</p>
                  <p style={{ fontSize: 13, color: MU, margin: '0 0 16px' }}>{plan.sublabel}</p>
                  <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 16, marginBottom: 16 }}>
                    {plan.features.map(f => (
                      <div key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <span style={{ color: G, fontSize: 11, fontWeight: 900, flexShrink: 0 }}>✓</span>
                        <span style={{ fontSize: 12, color: MU }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: MU }}>{t('university.gift.total', 'Total today')}</span>
                      <span style={{ fontSize: 24, fontWeight: 900, color: G }}>{plan.price}</span>
                    </div>
                    <p style={{ fontSize: 11, color: MU, marginTop: 8, lineHeight: 1.5 }}>
                      {t('university.gift.noRecurring', 'One-time payment · No subscription created for you')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    <UniversityFooter />
    </>
  )
}
